// Démarches guidées (F-058, F-059, D-19) : informations préparées, lien vers le service officiel, réalisation déclarée
// et datée par le bailleur. Aucune connexion aux portails, aucun identifiant demandé, aucune soumission automatique.
import { useState } from "react";
import { post, put } from "@core/api";
import { useGet } from "@core/hooks";
import { date, money, today } from "@core/format";
import { Badge, Button, Card, Checkbox, Empty, ErrorBox, Input, KeyValues, Loading, MoneyInput, Notice, PageHeader, Select, Tabs } from "@core/ui";
import { Amount, OfficialLink, useAction, useG } from "./shared";

const STEP_STATUS: Record<string, { label: string; tone: "neutre" | "succes" | "attention" }> = {
  a_faire: { label: "À faire", tone: "attention" },
  fait: { label: "Fait", tone: "succes" },
  sans_objet: { label: "Sans objet", tone: "neutre" },
};

export function DemarchesPage() {
  const [tab, setTab] = useState<"caf" | "occupation">("caf");
  return (
    <div className="page">
      <PageHeader
        eyebrow="Gestion"
        title="Démarches"
        intro="Bailivo prépare les informations utiles et vous oriente vers le service officiel. Vous effectuez la démarche vous-même, puis indiquez sa date de réalisation."
      />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: "caf", label: "CAF (Espace bailleur)" },
          { value: "occupation", label: "Déclaration d’occupation" },
        ]}
      />
      {tab === "caf" ? <Caf /> : <Occupation />}
    </div>
  );
}

function StepDone({ kind, leaseId, propertyId, year, state, label = "Indiquer comme fait" }: { kind: string; leaseId?: string; propertyId?: string; year?: number; state: any; label?: string }) {
  const { g, can } = useG();
  const { busy, run } = useAction();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ done_on: today(), note: "" });
  const st = STEP_STATUS[state?.status ?? "a_faire"];
  return (
    <div className="stack-sm">
      <span>
        <Badge tone={st.tone}>{st.label}</Badge> {state?.done_on ? <span className="small">le {date(state.done_on)}</span> : null} {state?.note ? <span className="small muted">· {state.note}</span> : null}
      </span>
      {can("operate") ? (
        open ? (
          <div className="form-row">
            <Input label="Date de réalisation" type="date" max={today()} value={f.done_on} onChange={(e) => setF({ ...f, done_on: e.target.value })} />
            <Input label="Note (numéro de dossier, accusé…)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
            <Button small busy={busy === "done"} onClick={async () => { const ok = await run("done", () => post(g("/steps"), { kind, lease_id: leaseId, property_id: propertyId, year, status: "fait", ...f }), "Réalisation enregistrée."); if (ok) setOpen(false); }}>
              Enregistrer
            </Button>
          </div>
        ) : (
          <div className="gestion-actions">
            <Button variant="secondaire" small onClick={() => setOpen(true)}>
              {label}
            </Button>
            {state?.status !== "sans_objet" ? (
              <Button variant="discret" small busy={busy === "na"} onClick={() => run("na", () => post(g("/steps"), { kind, lease_id: leaseId, property_id: propertyId, year, status: "sans_objet" }), "Démarche marquée sans objet.")}>
                Sans objet
              </Button>
            ) : null}
          </div>
        )
      ) : null}
    </div>
  );
}

function Caf() {
  const { g } = useG();
  const q = useGet<any>(g("/steps/caf"));
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data;
  return (
    <>
      <Notice>{d.notice}</Notice>
      <p>
        <OfficialLink href={d.official_url}>Ouvrir la page officielle de l’Espace bailleur CAF</OfficialLink>
      </p>
      {d.items.length === 0 ? <Empty title="Aucun bail en cours" /> : null}
      {d.items.map((i: any) => (
        <CafLease key={i.lease_id} i={i} />
      ))}
    </>
  );
}

function CafLease({ i }: { i: any }) {
  const { g, can } = useG();
  const { busy, run } = useAction();
  const [tp, setTp] = useState<{ active: boolean; monthly: number | null; since: string }>({ active: i.tiers_payant.active, monthly: i.tiers_payant.monthly_cents, since: i.tiers_payant.since ?? "" });
  const p = i.prepared;
  return (
    <Card title={i.property_label}>
      <h3>Informations préparées</h3>
      <KeyValues
        items={[
          ["Adresse du logement", p.adresse_du_logement || "—"],
          ["Locataire(s)", p.locataires.join(", ") || "—"],
          ["Date d’entrée", date(p.date_d_entree)],
          ["Loyer hors charges", money(p.loyer_hors_charges_cents)],
          [p.mode_de_charges === "forfait" ? "Charges forfaitaires" : "Provision pour charges", money(p.charges_cents)],
          ["Logement meublé", p.logement_meuble ? "Oui" : "Non"],
          ["Reste dû échu", <Amount key="r" cents={p.reste_du_echu_cents} />],
        ]}
      />
      <h3>Tiers payant</h3>
      {can("operate") ? (
        <div className="form-row">
          <Checkbox label="L’aide au logement est versée directement au bailleur" checked={tp.active} onChange={(v) => setTp({ ...tp, active: v })} />
          <MoneyInput label="Montant mensuel attendu" cents={tp.monthly} onChange={(v) => setTp({ ...tp, monthly: v })} help="Sert uniquement à reconnaître les versements de la CAF." />
          <Input label="Depuis le" type="date" value={tp.since} onChange={(e) => setTp({ ...tp, since: e.target.value })} />
          <Button variant="secondaire" small busy={busy === "tp"} onClick={() => run("tp", () => put(g(`/leases/${i.lease_id}/settings`), { caf_tiers_payant: tp.active, caf_monthly_cents: tp.monthly ?? undefined, caf_since: tp.since || undefined }), "Tiers payant enregistré.")}>
            Enregistrer
          </Button>
        </div>
      ) : (
        <p>{i.tiers_payant.active ? `Actif : ${money(i.tiers_payant.monthly_cents)} par mois` : "Non renseigné"}</p>
      )}
      {i.caf_payments.length ? (
        <p className="small">
          Versements CAF enregistrés : {i.caf_payments.map((x: any) => `${date(x.received_on)} ${money(x.amount_cents)}`).join(" · ")}
        </p>
      ) : null}
      <h3>Démarches</h3>
      <ul className="gestion-list">
        {i.steps.map((s: any) => (
          <li key={s.kind}>
            <div className="gestion-line">
              <span>
                <strong>{s.title}</strong> {s.relevant ? <Badge tone="attention">À examiner</Badge> : null}
                <span className="block small muted">{s.text}</span>
              </span>
            </div>
            <StepDone kind={s.kind} leaseId={i.lease_id} state={s.state} />
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Occupation() {
  const { g } = useG();
  const [year, setYear] = useState(new Date().getFullYear());
  const q = useGet<any>(g(`/steps/occupation?year=${year}`));
  const years = Array.from({ length: 5 }, (_, i) => String(new Date().getFullYear() - i)).map((v) => ({ value: v, label: v }));
  return (
    <>
      <Select label="Année" value={String(year)} onChange={(e) => setYear(Number(e.target.value))} options={years} />
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data ? (
        <>
          <Notice>{q.data.notice}</Notice>
          <p>
            <OfficialLink href={q.data.official_url}>Consulter les informations à déclarer sur impots.gouv.fr</OfficialLink>
          </p>
          <Card title="Informations à préparer">
            <ul>
              {q.data.to_declare.map((t: string) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </Card>
          {q.data.items.map((p: any) => (
            <Card key={p.property_id} title={p.property_label}>
              <p className="small muted">
                {p.address || "Adresse non renseignée"} · {p.furnished ? "meublé" : "non meublé"}
              </p>
              <p>{p.situation}</p>
              {p.occupants.length ? (
                <ul className="gestion-list">
                  {p.occupants.map((o: any) => (
                    <li key={o.lease_id}>
                      {(o.tenants ?? []).join(", ") || "Occupant non renseigné"} · {o.regime === "meuble" ? "location meublée" : "location"} · du {date(o.start_on)}
                      {o.end_on ? ` au ${date(o.end_on)}` : " (en cours)"} · loyer {money(o.rent_cents)}
                    </li>
                  ))}
                </ul>
              ) : null}
              <StepDone kind="declaration_occupation" propertyId={p.property_id} year={q.data.year} state={p.state} label="Indiquer la déclaration comme faite" />
            </Card>
          ))}
        </>
      ) : null}
    </>
  );
}
